import { z } from 'zod';

/**
 * Provider types the registry knows how to instantiate. Adding a provider = add a type here and a
 * factory in `@af/agent-core/registry`. `openai_compatible` covers vLLM, LM Studio, Together, Groq, etc.
 */
export const PROVIDER_TYPES = [
  'anthropic',
  'openai',
  'xai',
  'openrouter',
  'ollama',
  'openai_compatible',
] as const;
export const ProviderType = z.enum(PROVIDER_TYPES);
export type ProviderType = z.infer<typeof ProviderType>;

export const PROVIDER_META: Record<
  ProviderType,
  {
    label: string;
    needsApiKey: boolean;
    needsBaseUrl: boolean;
    defaultBaseUrl?: string;
    modelsDevId?: string;
  }
> = {
  anthropic: { label: 'Anthropic', needsApiKey: true, needsBaseUrl: false, modelsDevId: 'anthropic' },
  openai: { label: 'OpenAI', needsApiKey: true, needsBaseUrl: false, modelsDevId: 'openai' },
  xai: { label: 'xAI (Grok)', needsApiKey: true, needsBaseUrl: false, modelsDevId: 'xai' },
  openrouter: { label: 'OpenRouter', needsApiKey: true, needsBaseUrl: false, modelsDevId: 'openrouter' },
  ollama: {
    label: 'Ollama',
    needsApiKey: false,
    needsBaseUrl: true,
    defaultBaseUrl: 'http://127.0.0.1:11434',
  },
  openai_compatible: { label: 'OpenAI-compatible', needsApiKey: false, needsBaseUrl: true },
};

/** Model reference as stored on an agent version: `<providerSlug>:<modelId>`. */
export const ModelRef = z.string().regex(/^[a-z0-9][a-z0-9-]{0,40}:[\w./:@-]{1,120}$/);
export type ModelRef = z.infer<typeof ModelRef>;

export function parseModelRef(ref: string): { provider: string; model: string } {
  const i = ref.indexOf(':');
  if (i <= 0) throw new Error(`Invalid model ref: ${ref}`);
  return { provider: ref.slice(0, i), model: ref.slice(i + 1) };
}

/** Prices in USD per 1M tokens. */
export const ModelPrice = z.object({
  input: z.number().min(0),
  output: z.number().min(0),
  cacheRead: z.number().min(0).optional(),
  cacheWrite: z.number().min(0).optional(),
  /** Optional tier above a prompt-size threshold (e.g. >200k context). */
  tiers: z
    .array(
      z.object({
        aboveInputTokens: z.number().int().min(0),
        input: z.number().min(0),
        output: z.number().min(0),
        cacheRead: z.number().min(0).optional(),
        cacheWrite: z.number().min(0).optional(),
      }),
    )
    .optional(),
});
export type ModelPrice = z.infer<typeof ModelPrice>;
